import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Lock, Unlock, Save, FileDown, FileSpreadsheet, FileText, CalendarDays, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useActiveSchool } from "@/lib/activeSchool";
import { fundApi } from "@/lib/setupApi";
import { MONTH_OPTIONS } from "@/lib/mdcf";
import { pickAnchor, consumedForMonth as cascadeConsumed, cascadeOpening, buildFullLedger, isNewFundCascade } from "@/lib/fundCascade";
import MobileFundManagement from "@/components/poshan/MobileFundManagement";
import RiceReceiptsDialog from "@/components/poshan/RiceReceiptsDialog";
import StockAdjustmentDialog from "@/components/poshan/StockAdjustmentDialog";
import FundAdjustmentDialog from "@/components/poshan/FundAdjustmentDialog";
import { adjOf, decOf, round, GROUPS, NEG_MSG, noNegProps, AdjNote } from "@/lib/fundInput";

function todayParts() {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const dd = d.getDate();
  // IMPORTANT: build YYYY-MM-DD from LOCAL date parts, NOT toISOString (avoids timezone roll-over).
  const dt = `${y}-${String(m).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  return { y, m, dt };
}

const numStr = (v, dec = 2) => (Number(v) || 0).toFixed(dec);

const CLASS_KEYS = ["balVatika", "primary", "upperPrimary", "highSchool"];
const CLASS_LABELS = {
  balVatika: "Bal Vatika",
  primary: "Primary",
  upperPrimary: "Upper Primary",
  highSchool: "High School",
};

function emptyClasswise() {
  return { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0 };
}

function emptyRecord(schoolId, year, month) {
  return {
    schoolId,
    year,
    month,
    openingFund: emptyClasswise(),
    receivedFund: emptyClasswise(),
    openingRice: emptyClasswise(),
    receivedRice: emptyClasswise(),
    notes: "",
    isLocked: false,
  };
}

function mergeEdits(before, base, next) {
  const merged = { ...next };
  GROUPS.forEach((g) => {
    merged[g] = { ...next[g] };
    CLASS_KEYS.forEach((k) => {
      if (Number(before[g]?.[k] || 0) === Number(base[g]?.[k] || 0)) return;
      merged[g][k] = round(Number(before[g][k] || 0) - adjOf(before, g, k) + adjOf(next, g, k), decOf(g));
    });
  });
  return merged;
}

function sumCls(obj) {
  return CLASS_KEYS.reduce((s, k) => s + (Number(obj?.[k]) || 0), 0);
}

// Class-wise consumed (fund + rice) for the (year, month) of this school.
// Thin wrapper around the shared cascade helper so the UI keeps its old shape:
// { cost, rice }.
async function consumedForMonth(sid, y, m) {
  return cascadeConsumed(sid, y, m);
}

export default function FundManagement() {
  const { active, profiles } = useActiveSchool();
  const schoolId = active?.id || "";
  const tp = useMemo(todayParts, []);
  const [year, setYear] = useState(tp.y);
  const [month, setMonth] = useState(tp.m);
  const [date, setDate] = useState(tp.dt);
  const [record, setRecord] = useState(emptyRecord("", tp.y, tp.m));
  const [allBalances, setAllBalances] = useState([]);
  const [cascadedLedger, setCascadedLedger] = useState(new Map());
  // Every month from the anchor onward (saved or "not saved · auto").
  const ledgerRows = useMemo(() => [...cascadedLedger.values()], [cascadedLedger]);
  const [saving, setSaving] = useState(false);
  const [editingOpening, setEditingOpening] = useState(false);
  const [consumed, setConsumed] = useState({ cost: emptyClasswise(), rice: emptyClasswise() });
  // iter155 — Dated Rice Receipts dialog
  const [receiptsDialogOpen, setReceiptsDialogOpen] = useState(false);
  // iter157 — Stock adjustment dialog (later months only)
  const [stockAdjOpen, setStockAdjOpen] = useState(false);

  const yearChoices = useMemo(() => [tp.y - 2, tp.y - 1, tp.y, tp.y + 1], [tp.y]);

  useEffect(() => {
    if (!schoolId) { setAllBalances([]); setCascadedLedger(new Map()); return; }
    let cancelled = false;
    (async () => {
      try {
        const list = await fundApi.list(schoolId);
        if (cancelled) return;
        setAllBalances(list || []);
        // Build cascaded ledger so the History table shows transitive
        // openings/closings (each row's opening = previous row's closing).
        const ledger = await buildFullLedger(schoolId, list || []);
        if (!cancelled) setCascadedLedger(ledger);
      } catch (err) { console.error(err); if (!cancelled) { setAllBalances([]); setCascadedLedger(new Map()); } }
    })();
    return () => { cancelled = true; };
  }, [schoolId]);

  // iter156 — Determine whether this month is the chain anchor (no
  // earlier saved month exists for the school). Only the anchor allows a
  // typed opening balance; later months always inherit the cascaded
  // closing of the previous month.
  const anchorRec = useMemo(() => pickAnchor([...(allBalances || [])].sort((a, b) => a.year - b.year || a.month - b.month)), [allBalances]);
  const isAnchorMonth = useMemo(() => {
    if (!anchorRec) return true;
    return year < anchorRec.year || (year === anchorRec.year && month <= anchorRec.month);
  }, [anchorRec, year, month]);
  const [fundAdjOpen, setFundAdjOpen] = useState(false);
  // Previous saved month label — surfaced in the read-only note.
  const prevMonthLabel = useMemo(() => {
    const py = month === 1 ? year - 1 : year;
    const pm = month === 1 ? 12 : month - 1;
    return `${MONTH_OPTIONS.find((m) => m.v === pm)?.en || pm} ${py}`;
  }, [year, month]);

  // iter156 — Single source of truth for "load this month". Called by both
  // the initial load effect AND handleSave so the UI is always exactly
  // what the server returned. Returns {record, isLocked} for callers that
  // need to decide follow-up UI state.
  // Guards against a slower, older month's load overwriting the selected month.
  const loadSeq = useRef(0);
  const loadedRef = useRef(null);
  // Unsaved edits to re-apply on reloads after an adjustment save.
  const pendingRef = useRef(null);
  useEffect(() => { pendingRef.current = null; }, [schoolId, year, month]);
  const [blank, setBlank] = useState({});
  const [loadingMonth, setLoadingMonth] = useState(false);
  const loadMonth = useCallback(async () => {
    if (!schoolId) return null;
    const seq = ++loadSeq.current;
    const stale = () => seq !== loadSeq.current;
    setLoadingMonth(true);
    const cur = await fundApi.get(schoolId, year, month).catch(() => null);
    const prevYear = month === 1 ? year - 1 : year;
    const prevMonth = month === 1 ? 12 : month - 1;
    const cache = new Map();
    const { cascadeClosing } = await import("@/lib/fundCascade");
    const prevClosing = await cascadeClosing(schoolId, prevYear, prevMonth, cache);
    const cascadeOp = prevClosing ? { fund: prevClosing.fund, rice: prevClosing.rice } : null;
    if (cur) {
      // Anchor month → use STORED opening (what the user typed and the
      // server locked). Later months → inherit cascaded closing.
      const earlierExists = !!anchorRec && (anchorRec.year < year || (anchorRec.year === year && anchorRec.month < month));
      const openingFund = earlierExists && cascadeOp
        ? cascadeOp.fund
        : { ...emptyClasswise(), ...(cur.openingFund || {}) };
      const openingRice = earlierExists && cascadeOp
        ? cascadeOp.rice
        : { ...emptyClasswise(), ...(cur.openingRice || {}) };
      const nextRecord = {
        ...emptyRecord(schoolId, year, month),
        ...cur,
        openingFund,
        receivedFund: { ...emptyClasswise(), ...(cur.receivedFund || {}) },
        openingRice,
        receivedRice: { ...emptyClasswise(), ...(cur.receivedRice || {}) },
      };
      if (stale()) return null;
      loadedRef.current = nextRecord; setBlank({});
      const p = pendingRef.current;
      setRecord(p && p.year === year && p.month === month ? mergeEdits(p.before, p.base, nextRecord) : nextRecord);
      setEditingOpening(false);
      setLoadingMonth(false);
      return nextRecord;
    }
    const seed = emptyRecord(schoolId, year, month);
    if (cascadeOp) {
      seed.openingFund = cascadeOp.fund;
      seed.openingRice = cascadeOp.rice;
    }
    if (stale()) return null;
    loadedRef.current = seed; setBlank({});
    setRecord(seed);
    setLoadingMonth(false);
    // Only the anchor shows editable opening on fresh load.
    const earlierExists = (allBalances || []).some(
      (b) => (b.year < year) || (b.year === year && b.month < month),
    );
    setEditingOpening(!earlierExists);
    return seed;
  }, [schoolId, year, month, allBalances]);

  // Load month record (or seed defaults from previous month's classwise closing)
  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) await loadMonth();
      } catch (err) { console.error(err); }
    })();
    return () => { cancelled = true; };
  }, [schoolId, year, month, loadMonth]);

  // Compute consumed classwise from saved Daily Entries
  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    consumedForMonth(schoolId, year, month).then((c) => { if (!cancelled) setConsumed(c); });
    return () => { cancelled = true; };
  }, [schoolId, year, month]);

  // Received cells show only the typed amount; adjustments stay separate.
  const cellValue = (group, key) => (blank[`${group}.${key}`] ? ""
    : round(Number(record[group]?.[key] || 0) - adjOf(record, group, key), decOf(group)));
  const updateCell = (group, key, value) => {
    if (String(value).trim().startsWith("-") || Number(value) < 0) { toast.error(NEG_MSG, { id: "fund-neg" }); return; }
    const isBlank = value === "";
    setBlank((b) => ({ ...b, [`${group}.${key}`]: isBlank }));
    setRecord((r) => ({ ...r, [group]: { ...r[group], [key]: round((isBlank ? 0 : Number(value) || 0) + adjOf(r, group, key), decOf(group)) } }));
  };
  // After an adjustment save, reload but keep cells the user changed and had not saved yet.
  const reloadKeepingEdits = async () => {
    const base = loadedRef.current;
    if (base && GROUPS.some((g) => CLASS_KEYS.some((k) => Number(record[g]?.[k] || 0) !== Number(base[g]?.[k] || 0)))) {
      pendingRef.current = { year, month, before: record, base };
      toast.message("Your unsaved changes were kept — tap Save to store them");
    }
    await loadMonth();
  };

  const closingFundCls = useMemo(() => {
    const out = {};
    CLASS_KEYS.forEach((k) => {
      out[k] = (Number(record.openingFund[k]) || 0) + (Number(record.receivedFund[k]) || 0) - (consumed.cost[k] || 0);
    });
    return out;
  }, [record.openingFund, record.receivedFund, consumed.cost]);

  const closingRiceCls = useMemo(() => {
    const out = {};
    CLASS_KEYS.forEach((k) => {
      out[k] = (Number(record.openingRice[k]) || 0) + (Number(record.receivedRice[k]) || 0) - (consumed.rice[k] || 0);
    });
    return out;
  }, [record.openingRice, record.receivedRice, consumed.rice]);

  const refreshAll = async () => {
    const list = await fundApi.list(schoolId);
    setAllBalances(list || []);
    setCascadedLedger(await buildFullLedger(schoolId, list || []));
    try {
      const { bumpDailyEntriesVersion } = await import("@/lib/masterDataBus");
      bumpDailyEntriesVersion();
    } catch { /* bus not loaded */ }
  };

  const handleSave = async () => {
    if (loadingMonth || record?.year !== year || record?.month !== month) {
      toast.error("Still loading this month — please wait · ଅପେକ୍ଷା କରନ୍ତୁ");
      return;
    }
    if (!schoolId) { toast.error("Pick an active school first"); return; }
    setSaving(true);
    try {
      // iter156 — Explicit intent flag. Only send unlockOpening=true when
      // the user has tapped "Modify Opening Balance" AND the record is
      // currently locked. Later months never carry this flag because
      // their opening is server-derived.
      const unlockOpening = Boolean(record.isLocked && editingOpening && isAnchorMonth);
      const num = (o) => Object.fromEntries(Object.entries(o || {}).map(([k, v]) => [k, Number(v) || 0]));
      const payload = {
        ...record, schoolId, year, month,
        openingFund: num(record.openingFund), receivedFund: num(record.receivedFund),
        openingRice: num(record.openingRice), receivedRice: num(record.receivedRice),
        isLocked: true,
        unlockOpening,
      };
      await fundApi.upsert(payload);
      pendingRef.current = null;
      // Re-load from the server so the UI never diverges from storage.
      await loadMonth();
      await refreshAll();
      // Nudge dependent reports (MDCF, Rice Running Report, Quarterly UC)
      // so their caches invalidate immediately.
      try {
        const { bumpDailyEntriesVersion } = await import("@/lib/masterDataBus");
        bumpDailyEntriesVersion();
      } catch { /* bus not loaded in tests */ }
      toast.success("Fund balance saved · ଫଣ୍ଡ ବ୍ୟାଲାନ୍ସ save ହୋଇଛି");
    } catch (err) {
      console.error(err);
      // iter156 — Honour the server's 409 with its bilingual message and
      // keep the user's typed values on screen (do NOT reload).
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail) {
        toast.error(detail);
      } else if ((status === 423 || status === 400) && detail) {
        toast.error(detail);
      } else {
        toast.error("Save failed · Save fail");
      }
    } finally {
      setSaving(false);
    }
  };

  // Build a clean printable HTML snapshot (header + month-wise classwise rows)
  // Used by Excel (.xls) and Word (.doc) exporters so both files include the
  // school identity, generated date and full ledger — same as the on-screen
  // table — without relying on visual DOM scraping.
  const buildExportRows = () => {
    return ledgerRows.flatMap((b) => {
      const monthName = MONTH_OPTIONS.find((m) => m.v === b.month)?.en || b.month;
      const led = cascadedLedger.get(`${b.year}-${b.month}`);
      return CLASS_KEYS.map((k, idx) => {
        const oF = led ? led.opening.fund[k] : Number(b.openingFund?.[k] || 0);
        const rF = led ? led.received.fund[k] : Number(b.receivedFund?.[k] || 0);
        const cF = led ? led.consumed.fund[k] : 0;
        const oR = led ? led.opening.rice[k] : Number(b.openingRice?.[k] || 0);
        const rR = led ? led.received.rice[k] : Number(b.receivedRice?.[k] || 0);
        const cR = led ? led.consumed.rice[k] : 0;
        return {
          year: idx === 0 ? b.year : "",
          month: idx === 0 ? monthName : "",
          cls: CLASS_LABELS[k],
          oF: numStr(oF), rF: numStr(rF), cF: numStr(cF), closeF: numStr(oF + rF - cF),
          mF: numStr(led?.consumed.mdm?.fund[k] ?? cF), dF: numStr(led?.consumed.dr?.fund[k] || 0),
          oR: numStr(oR, 3), rR: numStr(rR, 3), cR: numStr(cR, 3), closeR: numStr(oR + rR - cR, 3),
          mR: numStr(led?.consumed.mdm?.rice[k] ?? cR, 3), dR: numStr(led?.consumed.dr?.rice[k] || 0, 3),
        };
      });
    });
  };

  const buildExportHtml = () => {
    const rows = buildExportRows();
    const today = new Date().toLocaleDateString("en-GB");
    const tdNum = "border:1px solid #111;padding:5px 8px;text-align:right;font-family:Calibri,Arial,sans-serif;";
    const tdTxt = "border:1px solid #111;padding:5px 8px;font-family:Calibri,Arial,sans-serif;";
    const th = "border:1px solid #111;padding:6px 8px;background:#e9e2d3;font-family:Calibri,Arial,sans-serif;text-align:left;";
    const body = rows.length === 0
      ? `<tr><td colspan="15" style="${tdTxt}text-align:center;">No fund records yet.</td></tr>`
      : rows.map((r) => `<tr>
          <td style="${tdTxt}">${r.year}</td>
          <td style="${tdTxt}">${r.month}</td>
          <td style="${tdTxt}">${r.cls}</td>
          <td style="${tdNum}">${r.oF}</td>
          <td style="${tdNum}">${r.rF}</td>
          <td style="${tdNum}">${r.mF}</td>
          <td style="${tdNum}">${r.dF}</td>
          <td style="${tdNum}">${r.cF}</td>
          <td style="${tdNum}">${r.closeF}</td>
          <td style="${tdNum}">${r.oR}</td>
          <td style="${tdNum}">${r.rR}</td>
          <td style="${tdNum}">${r.mR}</td>
          <td style="${tdNum}">${r.dR}</td>
          <td style="${tdNum}">${r.cR}</td>
          <td style="${tdNum}">${r.closeR}</td>
        </tr>`).join("");
    return `
      <h2 style="font-family:Calibri,Arial,sans-serif;margin:0 0 4px 0;">PM POSHAN — Fund &amp; Rice Ledger</h2>
      <p style="font-family:Calibri,Arial,sans-serif;margin:0 0 10px 0;font-size:12px;">
        <b>School:</b> ${active?.schoolName || ""} &nbsp;|&nbsp;
        <b>UDISE:</b> ${active?.udiseCode || ""} &nbsp;|&nbsp;
        <b>Generated:</b> ${today}
      </p>
      <table style="border-collapse:collapse;font-size:12px;">
        <thead><tr>
          <th style="${th}">Year</th>
          <th style="${th}">Month</th>
          <th style="${th}">Class</th>
          <th style="${th}text-align:right;">Opening Fund (Rs)</th>
          <th style="${th}text-align:right;">Received Fund (Rs)</th>
          <th style="${th}text-align:right;">MDM Fund (Rs)</th>
          <th style="${th}text-align:right;">DR Fund (Rs)</th>
          <th style="${th}text-align:right;">Total Consumed Fund (Rs)</th>
          <th style="${th}text-align:right;">Closing Fund (Rs)</th>
          <th style="${th}text-align:right;">Opening Rice (kg)</th>
          <th style="${th}text-align:right;">Received Rice (kg)</th>
          <th style="${th}text-align:right;">MDM Rice (kg)</th>
          <th style="${th}text-align:right;">DR Rice (kg)</th>
          <th style="${th}text-align:right;">Total Consumed Rice (kg)</th>
          <th style="${th}text-align:right;">Closing Rice (kg)</th>
        </tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  };

  const downloadBlob = (content, fileName, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    const html = `<!doctype html><html><head><meta charset="utf-8" /></head><body>${buildExportHtml()}</body></html>`;
    downloadBlob(
      html,
      `fund-ledger-${active?.udiseCode || "school"}.xls`,
      "application/vnd.ms-excel;charset=utf-8",
    );
    toast.success("Excel file downloaded");
  };

  const exportWord = () => {
    const inner = buildExportHtml();
    const wrapped = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8" /><title>Fund Ledger</title></head><body>${inner}</body></html>`;
    downloadBlob(
      wrapped,
      `fund-ledger-${active?.udiseCode || "school"}.doc`,
      "application/msword;charset=utf-8",
    );
    toast.success("Word document downloaded");
  };

  const exportPdf = async () => {
    const node = document.getElementById("fund-pdf-area");
    if (!node) { toast.error("Nothing to export"); return; }
    toast.message("Generating PDF…");
    try {
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        windowWidth: node.scrollWidth,
      });
      const img = canvas.toDataURL("image/png");
      // Landscape A4 fits the wide ledger best.
      const pdf = new jsPDF("l", "mm", "a4");
      const pageW = 297;
      const pageH = 210;
      const margin = 8;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      const ratio = canvas.width / canvas.height;
      let w = maxW;
      let h = w / ratio;
      // Multi-page slice if the table is taller than one page.
      if (h <= maxH) {
        pdf.addImage(img, "PNG", margin, margin, w, h);
      } else {
        // Slice by source pixel height matching maxH on page.
        const pxPerMm = canvas.width / maxW;
        const sliceHeightPx = Math.floor(maxH * pxPerMm);
        let yPx = 0;
        let pageIdx = 0;
        while (yPx < canvas.height) {
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.min(sliceHeightPx, canvas.height - yPx);
          const ctx = sliceCanvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(canvas, 0, yPx, canvas.width, sliceCanvas.height, 0, 0, canvas.width, sliceCanvas.height);
          const sliceImg = sliceCanvas.toDataURL("image/png");
          const sliceMmH = sliceCanvas.height / pxPerMm;
          if (pageIdx > 0) pdf.addPage();
          pdf.addImage(sliceImg, "PNG", margin, margin, maxW, sliceMmH);
          yPx += sliceCanvas.height;
          pageIdx += 1;
        }
      }
      pdf.save(`fund-ledger-${active?.udiseCode || "school"}.pdf`);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("PDF export failed");
    }
  };

  if (profiles.length === 0) {
    return (
      <main className="min-h-screen bg-sand-50 text-ink-900">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-clay">Setup needed</p>
          <h1 className="mt-3 font-heading text-3xl font-semibold">Add a school first</h1>
          <p className="mt-3 text-ink-700">Open <Link to="/setup" className="font-semibold text-clay underline">Setup</Link> to add one.</p>
        </div>
      </main>
    );
  }

  const readonly = (record.isLocked && !editingOpening) || !isAnchorMonth;

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900">
      <Navbar />

      {/* Mobile-only (<md) layout. Reuses parent state via props. */}
      <MobileFundManagement
        active={active}
        onOpenFundAdj={anchorRec ? () => setFundAdjOpen(true) : null}
        onOpenStockAdj={anchorRec ? () => setStockAdjOpen(true) : null}
        year={year} month={month} date={date}
        setYear={setYear} setMonth={setMonth} setDate={setDate}
        yearChoices={yearChoices}
        MONTH_OPTIONS={MONTH_OPTIONS}
        record={record}
        consumed={consumed}
        readonly={readonly}
        editingOpening={editingOpening}
        setEditingOpening={setEditingOpening}
        isAnchorMonth={isAnchorMonth}
        prevMonthLabel={prevMonthLabel}
        updateCell={updateCell}
        cellValue={cellValue}
        handleSave={handleSave}
        saving={saving}
        loadingMonth={loadingMonth}
        closingFundCls={closingFundCls}
        closingRiceCls={closingRiceCls}
        exportPdf={exportPdf}
        exportExcel={exportExcel}
        exportWord={exportWord}
        allBalances={allBalances}
        cascadedLedger={cascadedLedger}
      />

      <div className="hidden md:block">
      <div className="no-print mx-auto max-w-7xl px-4 pt-10 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-clay">Fund Management</p>
        <h1 className="mt-2 font-heading text-4xl font-semibold tracking-tight sm:text-5xl">Classwise Fund &amp; Rice Ledger</h1>
        <p className="mt-3 max-w-3xl text-base leading-relaxed text-ink-700">
          Class-wise opening balance, received funds and closing balance for <b>{active?.schoolName}</b>.
          Once saved, opening balance is <b>locked</b> per class — receive amounts can still be edited.
          Consumed values pull from your saved Daily Entries; closing balance updates automatically.
        </p>
      </div>

      {/* Controls */}
      <section className="no-print mx-4 mt-6 rounded-xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:mx-6 sm:p-6 lg:mx-8" data-testid="fund-controls">
        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="mb-1.5 block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Year</span>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="fund-year-select" className="h-11 w-32 border-border bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{yearChoices.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Month</span>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="fund-month-select" className="h-11 w-44 border-border bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTH_OPTIONS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.en} / {m.odia}</SelectItem>)}</SelectContent>
            </Select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Reference Date</span>
            <input data-testid="fund-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 w-44 rounded-md border border-border bg-white px-3 text-sm" />
          </label>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button onClick={handleSave} size="sm" disabled={saving || loadingMonth} data-testid="fund-save-btn" className="gap-2 bg-clay text-white hover:bg-clay-dark">
              <Save className="h-3.5 w-3.5" /> Save & Lock
            </Button>
            <Button onClick={exportPdf} size="sm" variant="outline" data-testid="fund-pdf-btn" className="gap-2">
              <FileDown className="h-3.5 w-3.5" /> PDF
            </Button>
            <Button onClick={exportExcel} size="sm" variant="outline" data-testid="fund-excel-btn" className="gap-2">
              <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
            </Button>
            <Button onClick={exportWord} size="sm" variant="outline" data-testid="fund-word-btn" className="gap-2">
              <FileText className="h-3.5 w-3.5" /> Word
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="fund-lock-row">
          {/* iter156 — Only the chain anchor (first-ever saved month) can
              have a typed opening balance. Later months inherit from the
              previous month's closing automatically, so the "Modify" CTA
              would be misleading and is hidden. */}
          {!isAnchorMonth && (
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-moss/10 px-3 py-1 text-xs font-semibold text-moss-dark"
              data-testid="fund-auto-opening-badge"
            >
              <Lock className="h-3 w-3" /> {isNewFundCascade() ? <>Opening = closing of {prevMonthLabel} (auto) · ପୂର୍ବ ମାସର ଅବଶିଷ୍ଟ</> : <>Opening = saved value for this month · ସଞ୍ଚିତ ପ୍ରାରମ୍ଭିକ ଅବଶିଷ୍ଟ</>}
            </span>
          )}
          {!isAnchorMonth && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                const py = month === 1 ? year - 1 : year;
                const pm = month === 1 ? 12 : month - 1;
                setYear(py); setMonth(pm);
              }}
              data-testid="fund-view-prev-month-btn"
              className="gap-1 text-xs text-clay"
            >
              View {prevMonthLabel}
            </Button>
          )}
          {isAnchorMonth && record.isLocked && !editingOpening && (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-moss/15 px-3 py-1 text-xs font-semibold text-moss-dark" data-testid="fund-locked-badge">
                <Lock className="h-3 w-3" /> Opening balance locked for {MONTH_OPTIONS.find((m) => m.v === month)?.en} {year}
              </span>
              <Button onClick={() => setEditingOpening(true)} size="sm" variant="ghost" data-testid="fund-modify-opening-btn" className="gap-2 text-clay">
                <Unlock className="h-3 w-3" /> Modify Opening Balance
              </Button>
            </>
          )}
          {isAnchorMonth && !record.isLocked && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-ochre/15 px-3 py-1 text-xs font-semibold text-ochre">
              Opening balance editable until first save
            </span>
          )}
        </div>
      </section>

      {/* Classwise Fund Table */}
      <section className="mx-4 mt-6 rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:mx-6 sm:p-6 lg:mx-8" data-testid="fund-card-rs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-lg font-semibold">Fund (Rs.) — Classwise</h3>
          {!!anchorRec && (
            <Button variant="outline" size="sm" data-testid="fund-adj-btn" onClick={() => setFundAdjOpen(true)} className="gap-1">
              <AlertTriangle className="h-3.5 w-3.5" /> Fund Adjustment ({(record.fundAdjustments || []).length})
            </Button>
          )}
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-sm" data-testid="fund-rs-table">
            <thead className="bg-sand-100 text-left text-ink-700">
              <tr>
                <th className="border border-border p-2 text-xs font-semibold uppercase tracking-[0.16em]">Class</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">Opening</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">Received</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">MDM</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">DR</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">Total consumed</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">Closing (auto)</th>
              </tr>
            </thead>
            <tbody>
              {CLASS_KEYS.map((k) => (
                <tr key={k} className="border-t border-border">
                  <td className="border border-border p-2 font-semibold text-ink-900">{CLASS_LABELS[k]}</td>
                  <td className="border border-border p-1.5">
                    <input data-testid={`fund-rs-opening-${k}`} type="number" step="0.01" value={cellValue("openingFund", k)} min="0" {...noNegProps}
                      readOnly={readonly}
                      onChange={(e) => updateCell("openingFund", k, e.target.value)}
                      className={`h-9 w-full rounded border border-border px-2 text-right font-mono text-sm ${readonly ? "bg-sand-50 text-ink-700" : "bg-white"}`} />
                  </td>
                  <td className="border border-border p-1.5">
                    <input data-testid={`fund-rs-received-${k}`} type="number" step="0.01" value={cellValue("receivedFund", k)} min="0" {...noNegProps}
                      onChange={(e) => updateCell("receivedFund", k, e.target.value)}
                      className="h-9 w-full rounded border border-border bg-white px-2 text-right font-mono text-sm" />
                  </td>
                  <td className="border border-border p-2 text-right font-mono text-ink-900" data-testid={`fund-rs-mdm-${k}`}>{numStr(consumed.mdm?.cost?.[k] ?? consumed.cost[k])}</td>
                  <td className="border border-border p-2 text-right font-mono text-ink-900" data-testid={`fund-rs-dr-${k}`}>{numStr(consumed.dr?.cost?.[k] || 0)}</td>
                  <td className="border border-border p-2 text-right font-mono font-semibold text-ink-900" data-testid={`fund-rs-consumed-${k}`}>{numStr(consumed.cost[k])}</td>
                  <td className={`border border-border p-2 text-right font-mono font-bold ${closingFundCls[k] < 0 ? "text-destructive" : "text-clay-dark"}`} data-testid={`fund-rs-closing-${k}`}>{numStr(closingFundCls[k])}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-border bg-sand-100">
                <th className="border border-border p-2 text-left text-ink-700">Total</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rs-opening-total">{numStr(sumCls(record.openingFund))}</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rs-received-total">{numStr(CLASS_KEYS.reduce((t, k) => t + (Number(cellValue("receivedFund", k)) || 0), 0))}</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rs-mdm-total">{numStr(sumCls(consumed.mdm?.cost || consumed.cost))}</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rs-dr-total">{numStr(sumCls(consumed.dr?.cost))}</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rs-consumed-total">{numStr(sumCls(consumed.cost))}</th>
                <th className="border border-border p-2 text-right font-mono text-clay-dark" data-testid="fund-rs-closing-total">{numStr(sumCls(closingFundCls))}</th>
              </tr>
            </tbody>
          </table>
        </div>
        <AdjNote rec={record} group="receivedFund" unit="Rs" testId="fund-rs-adj-note" />
      </section>

      {/* Classwise Rice Table */}
      <section className="mx-4 mt-6 rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:mx-6 sm:p-6 lg:mx-8" data-testid="fund-card-rice">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-lg font-semibold">Rice (kg) — Classwise</h3>
          {/* iter155 — "Add actual date(s)" entry point. Opens the dialog that
              splits the monthly total into dated receipts (±0.001 kg tolerance,
              audit log, 7-day undo). Hidden until the user has saved at least
              a non-zero received total this month so there is something to split. */}
          <Button
            variant="outline"
            size="sm"
            data-testid="rice-split-receipts-btn"
            disabled={sumCls(record.receivedRice) <= 0}
            onClick={() => setReceiptsDialogOpen(true)}
            className="gap-1"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            {(record.riceReceipts || []).length > 0
              ? `Edit dated receipts (${(record.riceReceipts || []).length})`
              : "Add actual date(s)"}
          </Button>
          {/* iter157 — Stock Adjustment is a LATER-month-only correction
              path. The anchor month corrects via Modify Opening Balance. */}
          {!!anchorRec && (
            <Button
              variant="outline"
              size="sm"
              data-testid="stock-adj-btn"
              onClick={() => setStockAdjOpen(true)}
              className="gap-1"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              Stock Adjustment ({(record.stockAdjustments || []).length})
            </Button>
          )}
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-sm" data-testid="fund-rice-table">
            <thead className="bg-sand-100 text-left text-ink-700">
              <tr>
                <th className="border border-border p-2 text-xs font-semibold uppercase tracking-[0.16em]">Class</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">Opening</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">Received</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">MDM</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">DR</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">Total consumed</th>
                <th className="border border-border p-2 text-right text-xs font-semibold uppercase tracking-[0.16em]">Closing (auto)</th>
              </tr>
            </thead>
            <tbody>
              {CLASS_KEYS.map((k) => (
                <tr key={k} className="border-t border-border">
                  <td className="border border-border p-2 font-semibold text-ink-900">{CLASS_LABELS[k]}</td>
                  <td className="border border-border p-1.5">
                    <input data-testid={`fund-rice-opening-${k}`} type="number" step="0.001" value={cellValue("openingRice", k)} min="0" {...noNegProps}
                      readOnly={readonly}
                      onChange={(e) => updateCell("openingRice", k, e.target.value)}
                      className={`h-9 w-full rounded border border-border px-2 text-right font-mono text-sm ${readonly ? "bg-sand-50 text-ink-700" : "bg-white"}`} />
                  </td>
                  <td className="border border-border p-1.5">
                    <input data-testid={`fund-rice-received-${k}`} type="number" step="0.001" value={cellValue("receivedRice", k)} min="0" {...noNegProps}
                      onChange={(e) => updateCell("receivedRice", k, e.target.value)}
                      className="h-9 w-full rounded border border-border bg-white px-2 text-right font-mono text-sm" />
                  </td>
                  <td className="border border-border p-2 text-right font-mono text-ink-900" data-testid={`fund-rice-mdm-${k}`}>{numStr(consumed.mdm?.rice?.[k] ?? consumed.rice[k], 3)}</td>
                  <td className="border border-border p-2 text-right font-mono text-ink-900" data-testid={`fund-rice-dr-${k}`}>{numStr(consumed.dr?.rice?.[k] || 0, 3)}</td>
                  <td className="border border-border p-2 text-right font-mono font-semibold text-ink-900" data-testid={`fund-rice-consumed-${k}`}>{numStr(consumed.rice[k], 3)}</td>
                  <td className={`border border-border p-2 text-right font-mono font-bold ${closingRiceCls[k] < 0 ? "text-destructive" : "text-clay-dark"}`} data-testid={`fund-rice-closing-${k}`}>{numStr(closingRiceCls[k], 3)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-border bg-sand-100">
                <th className="border border-border p-2 text-left text-ink-700">Total</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rice-opening-total">{numStr(sumCls(record.openingRice), 3)}</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rice-received-total">{numStr(CLASS_KEYS.reduce((t, k) => t + (Number(cellValue("receivedRice", k)) || 0), 0), 3)}</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rice-mdm-total">{numStr(sumCls(consumed.mdm?.rice || consumed.rice), 3)}</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rice-dr-total">{numStr(sumCls(consumed.dr?.rice), 3)}</th>
                <th className="border border-border p-2 text-right font-mono text-ink-900" data-testid="fund-rice-consumed-total">{numStr(sumCls(consumed.rice), 3)}</th>
                <th className="border border-border p-2 text-right font-mono text-clay-dark" data-testid="fund-rice-closing-total">{numStr(sumCls(closingRiceCls), 3)}</th>
              </tr>
            </tbody>
          </table>
        </div>
        <AdjNote rec={record} group="receivedRice" unit="kg" testId="fund-rice-adj-note" />
      </section>

      {/* History — classwise totals collapsed per month */}
      <section id="fund-pdf-area" className="mx-4 mt-8 rounded-xl border border-border bg-white p-6 sm:mx-6 lg:mx-8 print:m-0 print:border-0 print:shadow-none">
        <div className="mb-4 flex items-baseline justify-between">
          <h3 className="font-heading text-lg font-semibold">Monthly Ledger — {active?.schoolName}</h3>
          <p className="text-xs text-ink-700">UDISE: {active?.udiseCode || "—"} · Total months: {ledgerRows.length} ({(allBalances || []).length} saved)</p>
        </div>
        <div className="overflow-x-auto">
          <table id="fund-table-print" className="w-full min-w-[1500px] border-collapse text-sm" data-testid="fund-history-table">
            <thead>
              <tr className="bg-sand-100 text-left text-ink-700">
                <th className="border border-border p-2">Year</th>
                <th className="border border-border p-2">Month</th>
                <th className="border border-border p-2">Class</th>
                <th className="border border-border p-2 text-right">Opening Fund</th>
                <th className="border border-border p-2 text-right">Received Fund</th>
                <th className="border border-border p-2 text-right">MDM Fund</th>
                <th className="border border-border p-2 text-right">DR Fund</th>
                <th className="border border-border p-2 text-right">Total Consumed Fund</th>
                <th className="border border-border p-2 text-right">Closing Fund</th>
                <th className="border border-border p-2 text-right">Opening Rice (kg)</th>
                <th className="border border-border p-2 text-right">Received Rice</th>
                <th className="border border-border p-2 text-right">MDM Rice</th>
                <th className="border border-border p-2 text-right">DR Rice</th>
                <th className="border border-border p-2 text-right">Total Consumed Rice</th>
                <th className="border border-border p-2 text-right">Closing Rice</th>
              </tr>
            </thead>
            <tbody>
              {ledgerRows.length === 0 && (
                <tr><td colSpan={15} className="border border-border p-6 text-center text-ink-700">No fund records yet. Save your first month above.</td></tr>
              )}
              {ledgerRows.flatMap((b) => {
                const mname = MONTH_OPTIONS.find((m) => m.v === b.month);
                const led = cascadedLedger.get(`${b.year}-${b.month}`);
                return CLASS_KEYS.map((k, idx) => {
                  // Cascaded values when the ledger is ready, otherwise fall back
                  // to raw stored values (transient on first paint).
                  const oF = led ? led.opening.fund[k] : Number(b.openingFund?.[k] || 0);
                  const rF = led ? led.received.fund[k] : Number(b.receivedFund?.[k] || 0);
                  const cF = led ? led.consumed.fund[k] : 0;
                  const oR = led ? led.opening.rice[k] : Number(b.openingRice?.[k] || 0);
                  const rR = led ? led.received.rice[k] : Number(b.receivedRice?.[k] || 0);
                  const cR = led ? led.consumed.rice[k] : 0;
                  const closeF = oF + rF - cF;
                  const closeR = oR + rR - cR;
                  return (
                    <tr key={`${b.year}-${b.month}-${k}`} className="border-t border-border hover:bg-sand-50" data-testid={`fund-row-${b.year}-${b.month}-${k}`}>
                      <td className="border border-border p-2">{idx === 0 ? b.year : ""}</td>
                      <td className="border border-border p-2">
                        {idx === 0 ? (mname?.en || b.month) : ""}
                        {idx === 0 && !b.saved && (
                          <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-slate-500" data-testid={`fund-row-auto-${b.year}-${b.month}`}>not saved · auto</span>
                        )}
                      </td>
                      <td className="border border-border p-2">{CLASS_LABELS[k]}</td>
                      <td className="border border-border p-2 text-right">{numStr(oF)}</td>
                      <td className="border border-border p-2 text-right">{numStr(rF)}</td>
                      <td className="border border-border p-2 text-right">{numStr(led?.consumed.mdm?.fund[k] ?? cF)}</td>
                      <td className="border border-border p-2 text-right" data-testid={`fund-row-drf-${b.year}-${b.month}-${k}`}>{numStr(led?.consumed.dr?.fund[k] || 0)}</td>
                      <td className="border border-border p-2 text-right">{numStr(cF)}</td>
                      <td className="border border-border p-2 text-right">{numStr(closeF)}</td>
                      <td className="border border-border p-2 text-right">{numStr(oR, 3)}</td>
                      <td className="border border-border p-2 text-right">{numStr(rR, 3)}</td>
                      <td className="border border-border p-2 text-right">{numStr(led?.consumed.mdm?.rice[k] ?? cR, 3)}</td>
                      <td className="border border-border p-2 text-right" data-testid={`fund-row-drr-${b.year}-${b.month}-${k}`}>{numStr(led?.consumed.dr?.rice[k] || 0, 3)}</td>
                      <td className="border border-border p-2 text-right">{numStr(cR, 3)}</td>
                      <td className="border border-border p-2 text-right">{numStr(closeR, 3)}</td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </section>

      </div>
      {/* End desktop-only block (hidden on mobile <md). */}

      <footer className="mx-auto max-w-7xl px-4 pb-12 pt-6 text-xs text-ink-700 sm:px-6 lg:px-8">
        Each row's Opening = previous month's Closing (cascaded across every saved month). Closing = Opening + Received − Consumed (from saved Daily Entries). Negative values appear in red.
      </footer>
      {receiptsDialogOpen && (
        <RiceReceiptsDialog
          fund={record}
          year={year}
          month={month}
          isHardLocked={false}
          onClose={() => setReceiptsDialogOpen(false)}
          onSaved={async () => {
            try {
              const cur = await fundApi.get(schoolId, year, month);
              if (cur) setRecord((r) => ({ ...r, ...cur }));
            } catch (_e) { /* ignore */ }
          }}
        />
      )}
      {fundAdjOpen && (
        <FundAdjustmentDialog
          fund={record}
          schoolId={schoolId}
          year={year}
          month={month}
          onClose={() => setFundAdjOpen(false)}
          onSaved={async () => { await reloadKeepingEdits(); await refreshAll?.(); }}
        />
      )}
      {stockAdjOpen && (
        <StockAdjustmentDialog
          fund={record}
          schoolId={schoolId}
          year={year}
          month={month}
          isHardLocked={false}
          onClose={() => setStockAdjOpen(false)}
          onSaved={async () => {
            await reloadKeepingEdits();
            try {
              const { bumpDailyEntriesVersion } = await import("@/lib/masterDataBus");
              bumpDailyEntriesVersion();
            } catch { /* bus not loaded */ }
          }}
        />
      )}
    </main>
  );
}
